import selectn from 'selectn'
import { common } from '../../common'
import modules from './modules'
import helpers from './helpers'
import modifiers from '../../../modifiers/cricket'
import { compose, isObjectEmpty, getTrackingImg, showBottomAdModule, getBet365 } from '../../../../utils/common'

const page = {
  /**
   * compose data for scorecard page
   * @param  {Array}  rostersNode   rosters node
   * @param  {Array}  allInnings    details of all innings
   * @param  {Number} currentPeriod current innings
   * @param  {String} gameState  game state
   * @param  {Object} matchFlags    flags for this match
   * @param  {Object} params    page params
   * @param {Object}  device    device info
   * @param {Number} stateTypeId stateTypeId
   * @return {Object}             scorecard data
   */
  getScorecard(rostersNode = [], allInnings, currentPeriod, gameState, matchFlags, params, device, stateTypeId) {
    let inningInfo = helpers.getOrderedInnings(rostersNode, allInnings, currentPeriod, gameState, matchFlags, params, device, stateTypeId)
    const scorecard = inningInfo
    return scorecard
  },

  /**
   * @param {Object} articleNode  article node
   * @param {Object} edition      edition config
   * @param {Boolean} isAppLite
   * return required data to fetch related markup for sCore
   * @return {Object} article data required to fetch rendered markup
   */
  getArticleInfo(articleNode = {}, edition, isAppLite) {
    const id = articleNode.id
    const headline = articleNode.headline
    let slug
    let href = '#'

    if (typeof headline === 'string') {
      slug = headline
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/\s/g, '-')
        .toLowerCase()
    }

    if(isAppLite) {
      href = `/story/_/id/${id}/${slug}`
    } else {
      href= common.buildURL(selectn('links.web.href', articleNode), edition.domain)
    }

    return {
      id: id || null,
      type: articleNode.type || null,
      headline: articleNode.headline || '',
      description: articleNode.description || '',
      source: articleNode.byline || '',
      date: articleNode.lastModified || '',
      // TODO: pick href based on device?
      href: href,
      video: articleNode.video,
      images: articleNode.images
    }
  }
}

export const gamePackage = {

  processPlayByPlay(res, edition, params, location) {
    const { query = {} } = location
    const page = selectn('data.commentary.pageIndex', res)
    const pageCount = selectn('data.commentary.pageCount', res)
    let items
    let tiebreaker

    const isTiebreaker = query.innings === 'superover' ? true : false

    if (isTiebreaker) {
      tiebreaker = selectn('data.commentary.items', res)
    } else {
      items = selectn('data.commentary.items', res)
    }
    const fullCommentary = modules.getCommentary(items, tiebreaker, params, query) || {}


    //get lec clips to be added to the app state
    let lecclips = modules.getLecClips(items || tiebreaker)

    // total page count
    fullCommentary.pageCount = pageCount

    // current commentary page number used by lazyload to identify next page to load
    fullCommentary.page = page

    // arrange overs by page number
    fullCommentary.oversByPage = { [page] : fullCommentary.overs }

    return { fullCommentary, lecclips }
  },

  processVideos(res) {
    const videosNode = selectn('data.videos', res) || []
    const { resultsCount, resultsLimit, resultsOffset } = selectn('data', res) || {}
    const allVideos = modules.getAllVideos(videosNode)
    const currentVideosCount = (allVideos.videos || []).length
    const hasMore = (resultsOffset + currentVideosCount) < resultsCount

    Object.assign(allVideos, {
      resultsCount,
      resultsLimit,
      resultsOffset,
      currentVideosCount,
      hasMore
    })

    return { allVideos }
  },

  processData(res, edition, params, location, appProps) {

    const isAppLite = appProps && appProps.isAppLite || false
    const siteType = appProps && appProps.siteType || ''
    const device = appProps && appProps.device || {}
    const query = common.getQuery(edition, params, location, appProps)
    const adblock = selectn('_adblock', query) === 'true' ? true : false
    const { timeZoneBucket, host, isMultiSports } = edition
    const isWebView = (params.webview === 'webview') ? true : false
    const hrefConfig = { showSport : isMultiSports ? true : ( (params.webview === 'webview') ? true : false ) }
    // Select Nodes from API response
    const matchTitle = selectn('data.header.title', res) || ''
    let competition = selectn('data.header.competitions[0]', res) || {}
    competition.name = selectn('data.header.name', res) || ' '
    competition.title = matchTitle || ''
    const timeValid = selectn('data.header.timeValid', res)
    const date = competition && competition.date || ''
    const leaguesNode = selectn('data.header.leagues', res) || []
    const league = helpers.getLeagueObject(leaguesNode) || {}
    const leagueName = league && league.name || ''
    const isTournament = selectn('data.header.league.isTournament', res)
    const season = selectn('data.header.season', res) || {}
    const year = season && season.year || 0
    const seasonLeagueSlug = season && season.slug || ''
    const rostersNode = selectn('data.rosters', res) || []
    const squadNode = selectn('data.squads', res) || null
    const leadersNode = selectn('data.leaders', res) || []
    const gameInfo = selectn('data.gameInfo', res) || {}
    let state = selectn('status.type.state', competition)
    const stateTypeId = selectn('status.type.id', competition)
    const presentLocalTime = selectn('status.presentLocalTime', competition)
    const notesNode = selectn('data.notes', res) || []
    const debutsNode = selectn('data.debuts', res) || []
    const sessionInfo = selectn('status.session', competition)
    const articleNode = selectn('data.article', res) || {}
    const matchDescription = selectn('data.header.description', res) || ''
    const matchLink = selectn('data.header.links[0].href', res) || ''
    const matchShortName = selectn('data.header.shortName', res) || ''
    const scorer = common.getNote(notesNode, 'livescorer') || ''
    const commentator = common.getNote(notesNode, 'livecommentator') || ''
    const ballsPerOver = common.getNote(notesNode, 'ballsperover') || ''
    const featuredAthletes = selectn('status.featuredAthletes', competition) || []
    const leagueSlug = league && league.slug || ''
    const slugOfLeague = seasonLeagueSlug ? seasonLeagueSlug : leagueSlug
    const odds = selectn('data.odds', res) || []
    const situationNode = selectn('data.situation', res) || []
    const scoringSource = helpers.getScoringSource(competition)
    const headerLinks = selectn('data.header.links', res) || ''
    const watchESPNLink = common.getWatchESPNLink(headerLinks, edition.userGeo, device)
    const tieBreaker = competition.tiebreaker || []
    const showSuperOver = (/^superover$/ig).test(query.innings) === true
    const editionDateFormat = edition.editionDateFormat || ''

    let bet365 = { showInEdition: false }
    const isRecent = competition.recent || false
    // gameStateActual give game state class as returns from API
    const gameStateActual = common.getGameState(state)

    // set state to 'in' if recently completed match
    if (isRecent === true && state === 'post') {
      state = 'in'
    }
    // Call helpers
    const matchFlags = helpers.getMatchFlags(competition, ballsPerOver)
    const currentPeriod = helpers.getCurrentPeriod(competition)
    const allInnings = helpers.getAllInnings(competition, params, query, true, matchFlags, hrefConfig)
    const gameState = common.getGameState(state)
    const summary = common.getGameSummary(competition)
    /**
     * Compose function to get cricket specific gamestrip.
     * common.getGameStrip is called with all arguments that are passed to
     * cricketGameStrip.
     * modifiers.gameStrip is called with result of common.getGameStrip
     */
    let cricketGameStrip = compose(modifiers.gameStrip, common.getGameStrip)
    // Call cricket specific gamestrip with appropriate data
    const gameStrip = cricketGameStrip(competition, matchDescription, gameState, params.sport, timeZoneBucket, device, edition.domain, timeValid, edition.language, editionDateFormat)

    const gameStateClass = common.getGameStateClass(gameState)
    let article = page.getArticleInfo(articleNode, edition, isAppLite)

    // Get new commentaries node from API
    let commsMap = competition.commentaries || {}
    // Set default value of commentary to competition.details
    let comms = competition.details || []
    // If commsMap is not an empty object, Convert commentary map to sorted array
    if (isObjectEmpty(commsMap) === false) {
      comms = common.prepareCommentary(commsMap)
    }

    // Call current innnings data after getting details of all innings
    const innings = helpers.getCurrentInnings(competition, comms, rostersNode, allInnings, sessionInfo, gameState, params, host, matchFlags, device)
    const editionSite = edition.site || ''
    const userGeoUC = (edition && edition.userGeo || '').toUpperCase()
    if (editionSite === 'espncricinfo' || editionSite === 'espnuk' || isWebView) {
      let oddNode = selectn('[0]', odds) || {} // odds are array of objects in summary api
      bet365 = getBet365(oddNode, odds, editionSite, situationNode, gameState, device, userGeoUC)
    }

    // Build data for modules
    const matchFormat = selectn('class.generalClassCard', competition) || ''
    const matchDetails = modules.getMatchDetails(gameInfo, notesNode, debutsNode, featuredAthletes, params, matchFormat, leaguesNode, edition)

    /**
     * Number of balls for which bbb commentary should appear.
     * For Lite App - 12 balls
     * For Web App - 18 balls
     * @type {Number}
     */
    let numBBBComms = isAppLite ? 12 : 18
    let partCommentary = modules.getCommentary(comms, competition.tiebreaker, params, query, scorer, commentator, gameState, true, presentLocalTime, numBBBComms, hrefConfig, matchFlags.updatesOnly)

    const rosters = modules.getRosters(squadNode, rostersNode, gameStrip.teams, notesNode)
    const scorecardSummary = modules.getScorecardSummary(leadersNode, rostersNode, allInnings, params, query, hrefConfig)

    // Build data for page/tabs
    const scorecard = page.getScorecard(rostersNode, allInnings, currentPeriod, gameState, matchFlags, params, device, stateTypeId)
    const polling = helpers.shouldPoll(date, gameState)
    let showGameDetailFooter = true
    let tossStatus = false
    // In pre game state, we need to show only toss info but not schedule as it is already a part of gamestrip
    if( gameState === 'pregame' ) {
      let toss = common.getNote(notesNode, 'toss') || ''
      tossStatus = toss.length ? true : false
      showGameDetailFooter = (toss.length > 0) ? true : false
    }
    const showScoreCellHeader = true
    const gameStateClassMobile = 'cscore--' + gameStateActual
    const gameStateClassFeature = 'cscore__' + gameState

    const showBtmAdModule = showBottomAdModule(params.pageType)

    const trackingImg = getTrackingImg(edition, params.pageType, leagueName, slugOfLeague, params.gameId, location.pathname, siteType, state, query, gameStrip.teams, '')
    let gameStatusText = common.getGameStatusText( params.sport, competition, '', timeValid ) || ''

    if (typeof gameStatusText === 'string') {
      gameStatusText = gameStatusText.toLowerCase().replace(' ', '')
    }

    let hiddenLinks
    let standings = {}
    const cmtryInns = helpers.getCmtryInns(allInnings, tieBreaker, params, hrefConfig)
    const selInnings = helpers.getSelectedInnings(allInnings, cmtryInns, query, showSuperOver)

    if (!competition.description) {
      competition.description = selInnings.innDisplayName
    }

    let processedData = {
      article,
      summary,
      showGameHeader: true,
      showGameDetailFooter,
      scorecardSummary,
      scorecard,
      rosters,
      matchFlags,
      matchDetails,
      innings,
      gameStrip,
      gameStateClass,
      gameStateClassMobile,
      gameStateClassFeature,
      gameState,
      gameStatusText,
      gameStateActual,
      stateTypeId,
      presentLocalTime,
      polling,
      showScoreCellHeader,
      matchShortName,
      trackingImg,
      tossStatus,
      showBtmAdModule,
      siteType,
      scoringSource,
      bet365,
      watchESPNLink,
      adblock
    }

    if (isAppLite === false) {
      const standingsNode = selectn('data.standings', res) || {}
      const h2hEvents = selectn('data.headToHeadGames[0].events', res) || []
      const h2hRef = selectn('data.headToHeadGames[0].team', res) || {}
      const matchcardsNode = selectn('data.matchcards', res) || []
      const videosNode = selectn('data.videos', res) || []
      const leagueUid = selectn('data.header.league.uid', res) || {}
      const newsNode = selectn('data.news', res) || {}

      // Call helpers
      const cmtryFilter = !showSuperOver ? helpers.getCmtryFilter(selInnings) : []
      const selCmtryFilter = !showSuperOver ? helpers.getSelCmtryFilter(cmtryFilter, query) : {}
      const allVideos = modules.getAllVideos(videosNode)

      const videoSlider = modules.getSliderVideos(article, videosNode, params, gameState, siteType)
      const gameBreakStatus = common.getGameBreakInfo(sessionInfo)

      const headToHead = modules.getHeadToHead(edition, h2hEvents, gameStrip.teams, h2hRef)
      const noteGroups = modules.getMatchNotes(notesNode, matchFlags, gameState, allInnings)
      const closeOfPlay = modules.getCloseOfPlay(notesNode)
      const comparison = modules.getComparisons(competition, matchFlags)
      const statistics = modules.getStatistics(rostersNode, leadersNode, params, gameState, currentPeriod, competition, matchFlags, query, hrefConfig, scoringSource, comparison)
      const playersToWatch = modules.getPlayersToWatch(leadersNode, gameStrip)
      const news = modules.getNews(newsNode, edition, params, query, hrefConfig)
      const matchCoverage = modules.getMatchCoverage(newsNode, edition, params, query, hrefConfig)
      // Get all videos from rosters node
      const rosterVideos = modules.getRosterVideos(rostersNode)
      const matchcards = helpers.getMatchCards(matchcardsNode)
      const detailsHeaderClass = common.getDetailsHeaderClass(matchDescription, params)
      const hawkEye = modules.getHawkEye(notesNode, device)
      let fastCastMeta = {
        enabled: false
      }
      /**
       * Fastcast is available only if liveAvailable flag is set to true &&
       * match is international.
       * matchFlags.liveAvailable === true && matchFlags.international === true
       *
       * For now, we reply on fastcastAvailable flag from API to enable fastcast
       * Update the topic name if available.
       */
      if (matchFlags.fastcastAvailable === true) {
        // Rely on the polling logic which takes care of game states & dates
        fastCastMeta.enabled = polling.enabled
        let gameId = params.gameId
        fastCastMeta.topic = `gp-cricket-${gameId}`
        polling.duration = 15000
      }

      standings = modules.getStandings( standingsNode, params, query, slugOfLeague, hrefConfig, year )
      hiddenLinks = helpers.isHidden({ standingsNode, article, partCommentary, statistics, videos: allVideos.videos, matchCoverage, hawkEye })

      processedData = Object.assign({}, processedData, {
        allInnings,
        statistics,
        standings,
        selInnings,
        playersToWatch,
        noteGroups,
        closeOfPlay,
        matchCoverage,
        matchcards,
        headToHead,
        leagueUid,
        cmtryFilter,
        selCmtryFilter,
        cmtryInns,
        news,
        allVideos,
        videoSlider,
        gameBreakStatus,
        comparison,
        detailsHeaderClass,
        rosterVideos,
        date,
        fastCastMeta,
        hawkEye,
        adblock
      })
    } else {
      hiddenLinks = helpers.isHidden({ standingsNode: false, article: {}, partCommentary: {}, statistics: false, videos: [], matchCoverage: false, hawkEye: {} })
    }

    const meta = helpers.getMetaData(params, competition, gameStrip, matchDetails, standings, gameState, league, matchDescription, matchTitle, matchLink, edition, season, hrefConfig, article, isTournament, leagueName, slugOfLeague)
    // Build the nav
    const links = modules.buildNav(params, query, hiddenLinks, gameState, slugOfLeague, hrefConfig, year)
    const analytics = helpers.getAnalytics(params, gameStrip.teams, meta, gameState, edition)

    return Object.assign({}, processedData, {
      partCommentary,
      meta,
      links,
      analytics
    })
  },

  page: page,

  modules: modules,

  helpers: helpers

}

export default gamePackage



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/cricket/gamepackage/index.js